import "@/App.css";
import "@/finance-fund-control-cleanup.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import SingaporePreliveReady from "@/pages/SingaporePreliveReady";
import { SG_PRELIVE_LOGIN_HEALTH_ONLY, SG_PRELIVE_READ_ONLY, postLoginPath } from "@/lib/sgPreliveMode";
import ErpDashboard from "@/pages/ErpDashboard";
import AnalyticsDashboard from "@/pages/AnalyticsDashboard";
import DeadstockDashboard from "@/pages/DeadstockDashboard";
import MasterSKU from "@/pages/MasterSKU";
import PurchaseList from "@/pages/PurchaseList";
import PurchaseForm from "@/pages/PurchaseForm";
import PurchaseDetail from "@/pages/PurchaseDetail";
import ReceivingList from "@/pages/ReceivingList";
import ReceivingForm from "@/pages/ReceivingForm";
import ReceivingDetail from "@/pages/ReceivingDetail";
import InventoryList from "@/pages/InventoryList";
import InventoryDetail from "@/pages/InventoryDetail";
import OrderImport from "@/pages/OrderImport";
import OrderList from "@/pages/OrderList";
import OrderDetail from "@/pages/OrderDetail";
import Checker from "@/pages/Checker";
import FinalVerification from "@/pages/FinalVerification";
import Dispatch from "@/pages/Dispatch";
import AdsPanel from "@/pages/AdsPanel";
import Accounts from "@/pages/Accounts";
import FinanceHistory from "@/pages/FinanceHistory";
import FinanceFundControlClean from "@/pages/FinanceFundControlClean";
import FinanceSettlement from "@/pages/FinanceSettlement";
import FinanceReceivablesOpening from "@/pages/FinanceReceivablesOpening";
import FinanceReceivablesReport from "@/pages/FinanceReceivablesReport";
import FinanceWithdrawal from "@/pages/FinanceWithdrawal";
import FinanceExpense from "@/pages/FinanceExpense";
import FinanceReports from "@/pages/FinanceReports";
import CancelRefundReturn from "@/pages/CancelRefundReturn";
import FinanceHealthAudit from "@/pages/FinanceHealthAudit";
import ErpHealth from "@/pages/ErpHealth";
import FinanceWalletMovement from "@/pages/FinanceWalletMovement";
import SupplierRefunds from "@/pages/SupplierRefunds";
import SupplierDeposits from "@/pages/SupplierDeposits";
import ImportHistory from "@/pages/ImportHistory";
import ImportDetail from "@/pages/ImportDetail";
import MasterToko from "@/pages/MasterToko";
import StoreMappingManagement from "@/pages/StoreMappingManagement";
import BulkOpeningHPP from "@/pages/BulkOpeningHPP";
import UserAccess from "@/pages/UserAccess";
import StockOpname from "@/pages/StockOpname";
import ManualSales from "@/pages/ManualSales";
import MasterKonsumen from "@/pages/MasterKonsumen";
import MasterSupplier from "@/pages/MasterSupplier";
import SupplierLedger from "@/pages/SupplierLedger";
import DeliveryOrder from "@/pages/DeliveryOrder";
import Invoice from "@/pages/Invoice";
import ReturnManagement from "@/pages/ReturnManagement";
import RepackManagementV3 from "@/pages/RepackManagementV3";
import CostingRecipe from "@/pages/CostingRecipe";

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="page-loading">Memuat...</div>;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function LoginRoute() {
  const { user } = useAuth();
  if (user && user !== null) return <Navigate to={postLoginPath()} replace />;
  return <Login />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ErrorBoundary>
          <Routes>
            {SG_PRELIVE_LOGIN_HEALTH_ONLY ? (
              <>
                <Route path="/login" element={<LoginRoute />} />
                <Route path="/prelive" element={<ProtectedRoute><SingaporePreliveReady /></ProtectedRoute>} />
                <Route path="*" element={<Navigate to="/prelive" replace />} />
              </>
            ) : SG_PRELIVE_READ_ONLY ? (
              <>
                <Route path="/login" element={<LoginRoute />} />
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<ProtectedRoute><ErpDashboard /></ProtectedRoute>} />
                <Route path="/products" element={<ProtectedRoute><MasterSKU /></ProtectedRoute>} />
                <Route path="/finance/reports" element={<ProtectedRoute><FinanceReports /></ProtectedRoute>} />
                <Route path="/accounts" element={<ProtectedRoute><Accounts /></ProtectedRoute>} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </>
            ) : (
              <>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/" element={<ProtectedRoute><MasterSKU /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><ErpDashboard /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
            <Route path="/analytics/deadstock" element={<ProtectedRoute><DeadstockDashboard /></ProtectedRoute>} />
            <Route path="/purchases" element={<ProtectedRoute><PurchaseList /></ProtectedRoute>} />
            <Route path="/purchases/new" element={<ProtectedRoute><PurchaseForm /></ProtectedRoute>} />
            <Route path="/purchases/:id" element={<ProtectedRoute><PurchaseDetail /></ProtectedRoute>} />
            <Route path="/purchases/:id/edit" element={<ProtectedRoute><PurchaseForm /></ProtectedRoute>} />
            <Route path="/receiving" element={<ProtectedRoute><ReceivingList /></ProtectedRoute>} />
            <Route path="/receiving/new/:purchaseId" element={<ProtectedRoute><ReceivingForm /></ProtectedRoute>} />
            <Route path="/receiving/:id" element={<ProtectedRoute><ReceivingDetail /></ProtectedRoute>} />
            <Route path="/inventory" element={<ProtectedRoute><InventoryList /></ProtectedRoute>} />
            <Route path="/inventory/opname" element={<ProtectedRoute><StockOpname /></ProtectedRoute>} />
            <Route path="/inventory/returns" element={<ProtectedRoute><ReturnManagement /></ProtectedRoute>} />
            <Route path="/inventory/repack" element={<ProtectedRoute><RepackManagementV3 /></ProtectedRoute>} />
            <Route path="/inventory/manual-sales" element={<ProtectedRoute><ManualSales /></ProtectedRoute>} />
            <Route path="/inventory/delivery-orders/:doId" element={<ProtectedRoute><DeliveryOrder /></ProtectedRoute>} />
            <Route path="/inventory/invoices/:invId" element={<ProtectedRoute><Invoice /></ProtectedRoute>} />
            <Route path="/inventory/:productId" element={<ProtectedRoute><InventoryDetail /></ProtectedRoute>} />
            <Route path="/orders" element={<ProtectedRoute><OrderImport /></ProtectedRoute>} />
            <Route path="/orders/list" element={<ProtectedRoute><OrderList /></ProtectedRoute>} />
            <Route path="/orders/:id" element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
            <Route path="/checker" element={<ProtectedRoute><Checker /></ProtectedRoute>} />
            <Route path="/final" element={<ProtectedRoute><FinalVerification /></ProtectedRoute>} />
            <Route path="/dispatch" element={<ProtectedRoute><Dispatch /></ProtectedRoute>} />
            <Route path="/marketplace-ads" element={<ProtectedRoute><AdsPanel /></ProtectedRoute>} />
            <Route path="/accounts" element={<ProtectedRoute><Accounts /></ProtectedRoute>} />
            <Route path="/accounts/history" element={<ProtectedRoute><FinanceHistory /></ProtectedRoute>} />
            <Route path="/accounts/fund-control" element={<ProtectedRoute><FinanceFundControlClean /></ProtectedRoute>} />
            <Route path="/finance" element={<ProtectedRoute><Navigate to="/accounts" replace /></ProtectedRoute>} />
            <Route path="/finance/reports" element={<ProtectedRoute><FinanceReports /></ProtectedRoute>} />
            <Route path="/finance/settlement" element={<ProtectedRoute><FinanceSettlement /></ProtectedRoute>} />
            <Route path="/finance/tanam-piutang" element={<ProtectedRoute><FinanceReceivablesOpening /></ProtectedRoute>} />
            <Route path="/finance/piutang" element={<ProtectedRoute><FinanceReceivablesReport /></ProtectedRoute>} />
            <Route path="/finance/withdrawal" element={<ProtectedRoute><FinanceWithdrawal /></ProtectedRoute>} />
            <Route path="/finance/expense" element={<ProtectedRoute><FinanceExpense /></ProtectedRoute>} />
            <Route path="/finance/wallet-movement" element={<ProtectedRoute><FinanceWalletMovement /></ProtectedRoute>} />
            <Route path="/finance/cancel-refund" element={<ProtectedRoute><CancelRefundReturn /></ProtectedRoute>} />
            <Route path="/finance/supplier-deposits" element={<ProtectedRoute><SupplierDeposits /></ProtectedRoute>} />
            {/* AP Opening historical cutoff route hidden pending separate accounting review. */}
            <Route path="/reports" element={<ProtectedRoute><FinanceHealthAudit /></ProtectedRoute>} />
            <Route path="/erp-health" element={<ProtectedRoute><ErpHealth /></ProtectedRoute>} />
            <Route path="/refunds" element={<ProtectedRoute><SupplierRefunds /></ProtectedRoute>} />
            <Route path="/imports" element={<ProtectedRoute><ImportHistory /></ProtectedRoute>} />
            <Route path="/imports/:batchId" element={<ProtectedRoute><ImportDetail /></ProtectedRoute>} />
            <Route path="/stores" element={<ProtectedRoute><MasterToko /></ProtectedRoute>} />
            <Route path="/store-mappings" element={<ProtectedRoute><StoreMappingManagement /></ProtectedRoute>} />
            <Route path="/inventory/opening-hpp" element={<ProtectedRoute><BulkOpeningHPP /></ProtectedRoute>} />
            <Route path="/inventory/costing" element={<ProtectedRoute><CostingRecipe /></ProtectedRoute>} />
            <Route path="/customers" element={<ProtectedRoute><MasterKonsumen /></ProtectedRoute>} />
            <Route path="/suppliers" element={<ProtectedRoute><MasterSupplier /></ProtectedRoute>} />
            <Route path="/suppliers/:id/ledger" element={<ProtectedRoute><SupplierLedger /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute><UserAccess /></ProtectedRoute>} />
              </>
            )}
          </Routes>
          </ErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;